import { useEffect, useState, type RefObject } from 'react';

const EMPTY_PROGRESS = 6 / 342;

function isTrackableInput(input: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (input instanceof HTMLInputElement) {
    const type = (input.type || 'text').toLowerCase();
    if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'reset') {
      return false;
    }
  }
  // Ignore password-manager / honeypot fields Clerk may inject
  if (input.tabIndex < 0 && input.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  return true;
}

function isFilled(input: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (input instanceof HTMLInputElement && input.type === 'checkbox') {
    return input.checked;
  }
  if (input instanceof HTMLInputElement && input.type === 'radio') {
    return input.checked;
  }
  return input.value.trim().length > 0;
}

/**
 * Tracks completion of visible Clerk SignUp form fields as a 0–1 ratio.
 * Re-measures on input/change and when Clerk re-renders the form.
 *
 * Ported from the pickem frontend so both apps show the same sign-up header.
 */
export function useClerkSignUpProgress(containerRef: RefObject<HTMLElement | null>): number {
  const [progress, setProgress] = useState(EMPTY_PROGRESS);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const measure = () => {
      const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        'input, textarea'
      );

      let total = 0;
      let filled = 0;

      fields.forEach(field => {
        if (!isTrackableInput(field)) return;
        // Radio groups: count the group once
        if (field instanceof HTMLInputElement && field.type === 'radio') {
          if (!field.name) {
            total += 1;
            if (field.checked) filled += 1;
            return;
          }
          const group = root.querySelectorAll<HTMLInputElement>(
            `input[type="radio"][name="${CSS.escape(field.name)}"]`
          );
          if (group[0] !== field) return;
          total += 1;
          if (Array.from(group).some(radio => radio.checked)) filled += 1;
          return;
        }

        total += 1;
        if (isFilled(field)) filled += 1;
      });

      if (total === 0) {
        setProgress(EMPTY_PROGRESS);
        return;
      }

      const ratio = filled / total;
      setProgress(filled === 0 ? EMPTY_PROGRESS : ratio);
    };

    measure();

    root.addEventListener('input', measure, true);
    root.addEventListener('change', measure, true);

    const observer = new MutationObserver(() => {
      measure();
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value', 'checked', 'type'],
    });

    // Clerk controlled inputs sometimes update without bubbling reliably
    const intervalId = window.setInterval(measure, 400);

    return () => {
      root.removeEventListener('input', measure, true);
      root.removeEventListener('change', measure, true);
      observer.disconnect();
      window.clearInterval(intervalId);
    };
  }, [containerRef]);

  return progress;
}
